import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, Trash2, Loader2, Mail, Phone, Home } from 'lucide-react';
import { toast } from 'sonner';

export default function ResidentsPage() {
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', unit_number: '', password: 'changeme123' });

  useEffect(() => { loadResidents(); }, []);

  const loadResidents = async () => {
    try { const res = await api.get('/admin/residents'); setResidents(res.data); }
    catch { toast.error('Failed to load residents'); }
    finally { setLoading(false); }
  };

  const addResident = async () => {
    if (!form.name || !form.email || !form.unit_number) { toast.error('Fill required fields'); return; }
    setSubmitting(true);
    try {
      await api.post('/admin/residents', form);
      toast.success('Resident added');
      setShowNew(false);
      setForm({ name: '', email: '', phone: '', unit_number: '', password: 'changeme123' });
      loadResidents();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const deleteResident = async (userId) => {
    try { await api.delete(`/admin/residents/${userId}`); toast.success('Resident removed'); loadResidents(); }
    catch { toast.error('Failed to delete'); }
  };

  const filtered = residents.filter(r =>
    r.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.email?.toLowerCase().includes(search.toLowerCase()) ||
    r.unit_number?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="loading-spinner" /></div>;

  return (
    <div data-testid="residents-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Residents</h1>
          <p className="text-sm text-slate-500 mt-1">{residents.length} registered residents</p>
        </div>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="add-resident-btn"><Plus size={14} /> Add Resident</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-['Outfit']">Add Resident</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div><Label className="text-xs mb-1 block">Full Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Resident name" data-testid="resident-name" /></div>
              <div><Label className="text-xs mb-1 block">Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="email@example.com" data-testid="resident-email" /></div>
              <div><Label className="text-xs mb-1 block">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+91..." data-testid="resident-phone" /></div>
              <div><Label className="text-xs mb-1 block">Unit Number *</Label><Input value={form.unit_number} onChange={e => setForm({...form, unit_number: e.target.value})} placeholder="e.g. A-101" data-testid="resident-unit" /></div>
              <div><Label className="text-xs mb-1 block">Default Password</Label><Input value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Default password" data-testid="resident-password" /></div>
              <Button onClick={addResident} disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-resident-btn">
                {submitting ? <Loader2 size={14} className="animate-spin mr-2" /> : null} Add Resident
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email or unit..." className="pl-9 text-sm max-w-md" data-testid="resident-search" />
      </div>

      <Card className="border-slate-100">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Name</TableHead>
                <TableHead className="text-xs">Contact</TableHead>
                <TableHead className="text-xs">Unit</TableHead>
                <TableHead className="text-xs">Joined</TableHead>
                <TableHead className="text-xs w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-400 py-8">No residents found</TableCell></TableRow>
              )}
              {filtered.map(r => (
                <TableRow key={r.user_id} data-testid={`resident-row-${r.user_id}`}>
                  <TableCell className="font-medium text-sm">{r.name}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs text-slate-500 flex items-center gap-1"><Mail size={10} /> {r.email}</span>
                      {r.phone && <span className="text-xs text-slate-500 flex items-center gap-1"><Phone size={10} /> {r.phone}</span>}
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="secondary" className="text-xs"><Home size={10} className="mr-1" />{r.unit_number || '-'}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => deleteResident(r.user_id)} className="text-red-500 hover:text-red-700 h-7 w-7 p-0" data-testid={`delete-resident-${r.user_id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
